import type { ApiNode } from '@goalbuddy/client';
import { useQueries } from '@tanstack/react-query';
import { CircleCheck, Flame, Layers, PartyPopper } from 'lucide-react';
import { useState } from 'react';
import { TypeIcon } from '@/components/node-meta';
import { Badge, Card, Empty, PageHeader, Select, Spinner } from '@/components/ui';
import { areaColor } from '@/lib/area';
import { api } from '@/lib/api';
import { useDetail, useNodes, useStructure } from '@/lib/hooks';
import { ancestorsOf } from '@/lib/tree';
import { fmtDate } from '@/lib/utils';

const startOfWeek = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

export function DonePage() {
  const [range, setRange] = useState<'week' | 'month' | 'all'>('week');
  const { data: done, isLoading } = useNodes({ status: ['done'], limit: 1000 });
  const { data: structure } = useStructure();
  const { data: allDone } = useNodes({ status: ['done'], include_done: true });
  const { open } = useDetail();
  const { data: routines } = useNodes({ type: ['routine'] });
  const stats = useQueries({
    queries: (routines ?? []).map((r) => ({
      queryKey: ['occurrences', r.id],
      queryFn: () => api.occurrences(r.id),
    })),
  });

  if (isLoading) return <Spinner />;
  const since =
    range === 'week'
      ? startOfWeek()
      : range === 'month'
        ? new Date(Date.now() - 30 * 86_400_000)
        : new Date(0);
  const items = (done ?? [])
    .filter((n) => n.completed_at && new Date(n.completed_at) >= since)
    .sort((a, b) => b.completed_at!.localeCompare(a.completed_at!));
  const all = [...(structure ?? []), ...(allDone ?? [])];

  // Group by area, then by goal/result path.
  const groups = new Map<string, { area: string; path: string; nodes: ApiNode[] }>();
  for (const n of items) {
    const anc = ancestorsOf(all, n.id);
    const area = anc.find((a) => a.type === 'area')?.title ?? 'No area';
    const path = anc
      .filter((a) => a.type === 'goal' || a.type === 'result')
      .map((a) => a.title)
      .join(' › ');
    const key = `${area}|${path}`;
    if (!groups.has(key)) groups.set(key, { area, path, nodes: [] });
    groups.get(key)!.nodes.push(n);
  }
  const byArea = new Map<string, { path: string; nodes: ApiNode[] }[]>();
  for (const g of groups.values()) byArea.set(g.area, [...(byArea.get(g.area) ?? []), g]);

  const bestStreak = Math.max(0, ...stats.map((q) => q.data?.stats?.streak ?? 0));
  const areaNode = (title: string) => (structure ?? []).find((n) => n.type === 'area' && n.title === title);

  return (
    <div className="space-y-5">
      <PageHeader title="Done" subtitle="Everything you finished. Look how far you got.">
        <Select
          value={range}
          onChange={(e) => setRange(e.target.value as typeof range)}
          className="w-40"
          aria-label="Range"
        >
          <option value="week">This week</option>
          <option value="month">Last 30 days</option>
          <option value="all">All time</option>
        </Select>
      </PageHeader>

      <div className="grid grid-cols-3 gap-3">
        {[
          {
            label: 'Done in range',
            value: items.length,
            icon: <PartyPopper size={16} className="text-primary" />,
          },
          { label: 'Areas moved', value: byArea.size, icon: <Layers size={16} className="text-ok" /> },
          { label: 'Best streak', value: `${bestStreak}d`, icon: <Flame size={16} className="text-sun" /> },
        ].map((t) => (
          <Card key={t.label} className="p-4">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-fg">
              {t.icon} {t.label}
            </div>
            <div className="mt-1 text-3xl font-semibold tabular-nums">{t.value}</div>
          </Card>
        ))}
      </div>

      {(routines ?? []).length > 0 && (
        <Card className="p-4">
          <h2 className="mb-2 text-sm font-semibold">Routines</h2>
          <ul className="space-y-1.5 text-sm">
            {routines!.map((r, i) => {
              const st = stats[i]?.data?.stats;
              return (
                <li key={r.id} className="flex justify-between gap-3">
                  <span>{r.title}</span>
                  {st && (
                    <span className="shrink-0 text-muted-fg">
                      {st.done} of {st.due} days · {st.streak} day streak
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {items.length === 0 && (
        <Empty icon={<PartyPopper size={20} />}>
          Nothing completed in this range yet. Finish one task and it shows up here.
        </Empty>
      )}
      {[...byArea.entries()].map(([area, gs]) => {
        const color = areaColor(structure, areaNode(area)?.id);
        return (
          <Card key={area} className="overflow-hidden">
            <div
              className="flex items-center gap-2 border-b border-border px-4 py-3"
              style={color ? { boxShadow: `inset 3px 0 0 ${color}` } : undefined}
            >
              <h2 className="font-semibold">{area}</h2>
              <Badge>{gs.reduce((n, g) => n + g.nodes.length, 0)}</Badge>
            </div>
            <div className="space-y-3 p-3">
              {gs.map((g) => (
                <div key={g.path}>
                  {g.path && <div className="px-2 pb-1 text-xs font-medium text-muted-fg">{g.path}</div>}
                  <ul>
                    {g.nodes.map((n) => (
                      <li key={n.id}>
                        <button
                          className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-muted/70"
                          onClick={() => open(n.id)}
                        >
                          <CircleCheck size={16} className="shrink-0 text-ok" />
                          {n.type !== 'task' && <TypeIcon type={n.type} />}
                          <span className="min-w-0 flex-1">{n.title}</span>
                          <span className="shrink-0 text-xs text-muted-fg">{fmtDate(n.completed_at)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
